import { ALMETLAA_ACTION, ALMETLAA_SCREEN } from '@/core/constants/dynatraceCatalog';
import { useTranslation } from '@/core/hooks';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import { loadAdhaTranslationsOnly } from '@/core/services/translationService';
import { useEffect, useRef } from 'react';
import { resolveAdhaUaePassUserMessage } from '@/modules/auth/constants/adhaUaePassError.map';
import { adhaUaePassService } from '@/modules/auth/services/adhaUaePassService';

type CompleteAdhaLoginWithCode = (oauthCode: string) => Promise<void>;

export interface UseAdhaWelcomeOAuthCallbackParams {
  completeAdhaLoginWithCode: CompleteAdhaLoginWithCode;
  setLoginDialogVisible: (visible: boolean) => void;
  setAdhaErrorDialogMessage: (message: string) => void;
  setAdhaErrorDialogVisible: (visible: boolean) => void;
  setAdhaOAuthProcessing: (processing: boolean) => void;
  clearAdhaLoginError?: () => void;
}

/**
 * Welcome (web): consume stashed `/uae-pass` callback and continue login or show mapped error.
 * - OAuth redirect errors → MessageOkDialog
 * - Login API `code` errors → MessageOkDialog (labels only; never API errorMessage)
 */
export function useAdhaWelcomeOAuthCallback({
  completeAdhaLoginWithCode,
  setLoginDialogVisible,
  setAdhaErrorDialogMessage,
  setAdhaErrorDialogVisible,
  setAdhaOAuthProcessing,
  clearAdhaLoginError,
}: UseAdhaWelcomeOAuthCallbackParams): { resetOAuthCallbackHandled: () => void } {
  // UAE PASS error copy is `adha`-tagged in Contentful; guests on the login
  // dialog don't have the ADHA tree merged yet, so load it before resolving.
  const { t, currentLanguage } = useTranslation(true);
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current) {
      return;
    }

    if (!adhaUaePassService.hasPendingRawCallback()) {
      setAdhaOAuthProcessing(false);
      return;
    }

    setAdhaOAuthProcessing(true);
    const pending = adhaUaePassService.consumeAndResolvePendingCallback();
    if (!pending) {
      setAdhaOAuthProcessing(false);
      return;
    }

    handledRef.current = true;
    setLoginDialogVisible(true);

    const showErrorDialog = (resolveMessage: () => string) =>
      loadAdhaTranslationsOnly(currentLanguage).finally(() => {
        setAdhaErrorDialogMessage(resolveMessage());
        setAdhaErrorDialogVisible(true);
        setAdhaOAuthProcessing(false);
      });

    if (pending.errorKey || pending.error) {
      const isCancel =
        pending.errorKey?.toLowerCase().includes('cancel') ||
        pending.error?.toLowerCase().includes('cancel');
      trackCatalogEvent({
        screen: ALMETLAA_SCREEN.AUTH,
        actionName: isCancel
          ? ALMETLAA_ACTION.AUTH.UAE_PASS_CANCEL
          : ALMETLAA_ACTION.AUTH.UAE_PASS_ERROR,
        eventType: isCancel ? 'action' : 'error',
        properties: pending.error
          ? { p_failedMessage: pending.error }
          : undefined,
      });
      void showErrorDialog(() =>
        pending.errorKey ? t(pending.errorKey) : pending.error!,
      );
      return;
    }

    if (pending.code) {
      completeAdhaLoginWithCode(pending.code)
        .then(() => {
          trackCatalogEvent({
            screen: ALMETLAA_SCREEN.AUTH,
            actionName: ALMETLAA_ACTION.AUTH.UAE_PASS_SUCCESS,
            eventType: 'action',
          });
        })
        .catch((err: unknown) => {
          trackCatalogEvent({
            screen: ALMETLAA_SCREEN.AUTH,
            actionName: ALMETLAA_ACTION.AUTH.UAE_PASS_ERROR,
            eventType: 'error',
            properties: {
              p_failedMessage: err instanceof Error ? err.message : String(err),
            },
          });
          clearAdhaLoginError?.();
          return showErrorDialog(() => resolveAdhaUaePassUserMessage(err, t));
        })
        .finally(() => {
          setAdhaOAuthProcessing(false);
        });
      return;
    }

    setAdhaOAuthProcessing(false);
  }, [
    clearAdhaLoginError,
    completeAdhaLoginWithCode,
    currentLanguage,
    setAdhaErrorDialogMessage,
    setAdhaErrorDialogVisible,
    setAdhaOAuthProcessing,
    setLoginDialogVisible,
    t,
  ]);

  return {
    resetOAuthCallbackHandled: () => {
      handledRef.current = false;
    },
  };
}
